"use client";

import { useAppDispatch, useAppSelector } from "@/lib/client/storeHooks";
import { formatMoney } from "@/lib/money-utils";
import { useTranslations } from "next-intl"
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { loadCart, addNewDeliveryAddress, setCurrentDeliveryAddressId } from "@/lib/client/cartSlice"
import { CartInfo } from "@/types";
import MapPin from "@/components/icons/map-pin-solid.svg";
import Plus from "@/components/icons/plus.svg";
import UserSolid from "@/components/icons/user-solid.svg";
import { Link, usePathname } from "@/i18n/navigation";
import AddressDisplay from "./address-display";
import Select from "react-select";
//import "@/components/ecommerce/css/react-select.css";

interface CartOptionsProps {
  lockUi?: boolean;
  setLockUi?: (s: boolean) => void;
  billingEmail?: string;
  companyName?: string;
}

const UPDATE_TIMEOUT = 1000;

export default function CartOptions(props: CartOptionsProps) {
  const { lockUi, setLockUi } = props;
  const t = useTranslations('Cart');
  const cart = useAppSelector(s => s.cart);
  const possibleDeliveryAddressOptions = useAppSelector(s => s.cart.possibleDeliveryAddreses);
  const currentDeliveryAddressId = useAppSelector(s => s.cart.currentDeliveryAddressId);
  const currentDeliveryAddressObj = useAppSelector(s => s.cart.possibleDeliveryAddreses.find(a => a.id === s.cart.currentDeliveryAddressId))
  const currentShippingMethod = useAppSelector(s => s.cart.shippingMethod);
  const session = useAppSelector(s => s.session);
  const dispatch = useAppDispatch();

  const pathname = usePathname();

  const referenceChangeTimoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [addressName, setAddressName] = useState<string>("");
  const [addressFirstName, setAddressFirstName] = useState<string>("");
  const [addressLastName, setAddressLastName] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [addressZip, setAddressZip] = useState<string>("");
  const [addressTown, setAddressTown] = useState<string>("");
  const [lockAddress, setLockAddress] = useState<boolean>(false);
  const [internalRef, setInternalRef] = useState<string>(cart.internalReference || "");

  // Reset local state when cart is cleared
  useEffect(() => {
    clearAddressForm();
    resetInternalReference();
    if (!(cart.shippingMethod && cart.shippingMethod.length > 0)) {
      doShippingMethodChange('PostB');
    }
  }, [cart.cartId]);

  // React-select options for shipping methods
  const shippingMethodOptions = cart.possibleShippingMethods.map(optionObj => {
    let label = "";
    if (optionObj.duringOpeningHours) {
      label = `${t(optionObj.labelTag)} (${t('duringOpeningHours')}, ${formatMoney(optionObj.cost)})`;
    } else {
      label = `${t(optionObj.labelTag)} (${optionObj.shippingDays} ${optionObj.shippingDays > 1 ? t('working_days') : t('working_day')}, ${formatMoney(optionObj.cost)})`;
    }
    return {
      value: optionObj.labelTag,
      label: label
    };
  });

  // React-select options for delivery addresses
  const deliveryAddressOptions = possibleDeliveryAddressOptions.map(addressObj => ({
    value: addressObj.id,
    label: addressObj.name
  }));

  const handleReferenceChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const handler = async () => {
      setLockUi && setLockUi(true);
      const res = await fetch('/api/cart/update-internal-reference', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: e.target.value }),
      })

      setLockUi && setLockUi(false);

      if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
      const data: CartInfo = await res.json();

      dispatch(loadCart(data));
    }

    if (referenceChangeTimoutRef.current) {
      clearTimeout(referenceChangeTimoutRef.current);
      referenceChangeTimoutRef.current = null;
    }

    referenceChangeTimoutRef.current = setTimeout(handler, UPDATE_TIMEOUT);
  };

  const handleReferenceKeyUp = async (e: KeyboardEvent) => {
    const newRef = (e.target as HTMLInputElement).value;

    const handler = async () => {
      setLockUi && setLockUi(true);
      const res = await fetch('/api/cart/update-internal-reference', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: newRef }),
      })

      setLockUi && setLockUi(false);

      if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
      const data: CartInfo = await res.json();

      dispatch(loadCart(data));
    }

    if (referenceChangeTimoutRef.current) {
      clearTimeout(referenceChangeTimoutRef.current);
      referenceChangeTimoutRef.current = null;
    }

    if (e.key === "Enter") {
      await handler();
    } else {
      referenceChangeTimoutRef.current = setTimeout(handler, UPDATE_TIMEOUT);
    }
  };

  const handleShippingMethodChange = async (selectedOption: { value: string, label: string } | null) => {
    if (selectedOption) {
      await doShippingMethodChange(selectedOption.value);
    }
  }

  const doShippingMethodChange = async (newMethod: string) => {
    setLockUi && setLockUi(true);
    const res = await fetch('/api/cart/update-delivery-method', {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deliveryMethod: newMethod }),
    })

    setLockUi && setLockUi(false);

    if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
    const data: CartInfo = await res.json();

    dispatch(loadCart(data));
  }

  const handleShippingAddressChange = async (selectedOption: { value: number, label: string } | null) => {
    if (selectedOption) {
      setLockUi && setLockUi(true);

      const res = await fetch('/api/cart/update-delivery-address', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addressId: selectedOption.value }),
      })

      setLockUi && setLockUi(false);

      if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
      //const data: CartInfo = await res.json();

      dispatch(setCurrentDeliveryAddressId(selectedOption.value));
    }
  };

  const handleAddressCreation = async () => {
    setLockUi && setLockUi(true);
    setLockAddress(true);
    let res = await fetch('/api/delivery-addresses/add', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: addressName,
        firstName: addressFirstName,
        lastName: addressLastName,
        addressLine1,
        addressLine2,
        zip: addressZip,
        city: addressTown,
        country: 'ch'
      }),
    });

    setLockAddress(false);
    if (!res.ok) {
      setLockUi && setLockUi(false);
      throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
    }
    const newAddress = await res.json();
    dispatch(addNewDeliveryAddress(newAddress));
    clearAddressForm();
    // Close the modal after successful address creation
    (document.getElementById('new_address_modal') as HTMLDialogElement).close();
    // Set the newly created address as the default delivery address
    const addressUpdateRes = await fetch('/api/cart/update-delivery-address', {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ addressId: newAddress.id }),
    });

    if (!addressUpdateRes.ok) {
      setLockUi && setLockUi(false);
      throw new Error(`HTTP ${addressUpdateRes.status}; ${JSON.stringify(await addressUpdateRes.json())}`);
    }

    const latestCartRes = await fetch(`/api/cart/`, {
      method: 'GET',
      headers: { "Content-Type": "application/json" },
    });

    if (!latestCartRes.ok) {
      setLockUi && setLockUi(false);
      throw new Error(`HTTP ${latestCartRes.status}; ${JSON.stringify(await latestCartRes.json())}`);
    }
    const latestCart: CartInfo = await latestCartRes.json();

    dispatch(loadCart(latestCart));
    setLockUi && setLockUi(false);
  };

  function canNewAddressBeSubmitted(): boolean {
    return (addressName.length > 0 && addressFirstName.length > 0 && addressLastName.length > 0 && addressLine1.length > 0 && addressZip.length == 4 && /^[1-9]\d{3}$/.test(addressZip) && addressTown.length >= 2)
  }

  function clearAddressForm() {
    setAddressName("");
    setAddressFirstName("");
    setAddressLastName("");
    setAddressLine1("");
    setAddressLine2("");
    setAddressZip("");
    setAddressTown("");
  }

  function resetInternalReference() {
    setInternalRef("");
  }

  return (
    <div className="w-full flex flex-col gap-2.5">
      <div className="py-2 w-full">
        <div className="font-bold font-[Outfit] text-sm">
          {t('cart-internal-reference-label')}
        </div>
      </div>
      <div className="px-4 py-2">
        <input
          className="input"
          type="text"
          placeholder={t('cart-internal-reference')}
          defaultValue={internalRef}
          onKeyUp={handleReferenceKeyUp}
          onChange={handleReferenceChange}
        />
      </div>
      <div className="py-2 w-full">
        <div className="font-bold font-[Outfit] text-sm">
          {t('cart-delivery-options')}
        </div>
      </div>
      <div className="px-4 py-2 gap-4 flex flex-col">
        <fieldset className="fieldset p-0">
          <legend className="fieldset-legend text-xs text-base-content/60 font-light font-[Outfit] pt-0 pb-2.5">{t('cart-delivery-options-question')}</legend>
          <Select
            className="w-80 text-base"
            options={shippingMethodOptions}
            value={shippingMethodOptions.find(option => option.value === currentShippingMethod) || null}
            onChange={handleShippingMethodChange}
            classNamePrefix="react-select"
          />
        </fieldset>
        <span className="label h-2 text-xs text-base-content/60 font-light font-[Outfit]">{t('cart-delivery-options-hint')}</span>
      </div>
      <div className="py-2 w-full">
        <div className="font-bold font-[Outfit] text-sm">
          {t('cart-delivery-address')}
        </div>
      </div>
      <div className="px-4 py-2 flex flex-col gap-4">
        <div className="flex flex-row gap-4">
          {possibleDeliveryAddressOptions.length > 0 && (
            <Select
              className="w-80 text-base"
              options={deliveryAddressOptions}
              value={deliveryAddressOptions.find(option => option.value === currentDeliveryAddressId) || null}
              onChange={handleShippingAddressChange}
              placeholder={t('cart-delivery-address-choose')}
              classNamePrefix="react-select"
            />
          )}
          <button className="btn rounded-full" onClick={() => (document.getElementById('new_address_modal') as HTMLDialogElement).showModal()}>
            <MapPin height={14} width={14} />
            {t('cart-delivery-address-new')}
          </button>
        </div>
        {currentDeliveryAddressObj && (
          <AddressDisplay
            className="leading-5 text-sm"
            addressLine1={currentDeliveryAddressObj.addressLine1}
            addressLine2={currentDeliveryAddressObj.addressLine2}
            zip={currentDeliveryAddressObj.zip}
            city={currentDeliveryAddressObj.city}
          />
        )}
      </div>
      <div className="py-2 w-full">
        <div className="font-bold font-[Outfit] text-sm">
          {t('cart-payment-method')}
        </div>
      </div>
      <div className="px-4 py-2 flex flex-col gap-4 text-sm">
        <div className="leading-5">
          {cart.paymentTerms!.TermCode === "00" ?
            t('cart-payment-method-electronic') :
            `${t('cart-payment-method-bill')}${props.billingEmail ? ` (${props.billingEmail})` : ''}`
          }
        </div>
        <div className="leading-5">
          {t(`cart-payment-delay-${cart.paymentTerms!.TermCode}`)}
        </div>
      </div>
      <dialog id="new_address_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg font-[Outfit]">{t('cart-delivery-address-new-title')}</h3>
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          {session.user && session.user.id ? (
            <div className="flex flex-col items-center gap-2.5 w-full pt-4">
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-name')} onChange={e => setAddressName(e.target.value)} value={addressName} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              {props.companyName && (
                <label className="input w-full">
                  <MapPin height={14} width={14} className="fill-base-content" />
                  <input className="grow" type="text" placeholder={t('cart-delivery-address-new-company')} value={props.companyName} readOnly={true} />
                </label>
              )}
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-firstName')} onChange={e => setAddressFirstName(e.target.value)} value={addressFirstName} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-lastName')} onChange={e => setAddressLastName(e.target.value)} value={addressLastName} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-addressLine1')} onChange={e => setAddressLine1(e.target.value)} value={addressLine1} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-addressLine2')} onChange={e => setAddressLine2(e.target.value)} value={addressLine2} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-zip')} onChange={e => setAddressZip(e.target.value)} value={addressZip} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <label className="input w-full">
                <MapPin height={14} width={14} className="fill-base-content" />
                <input className="grow" type="text" placeholder={t('cart-delivery-address-new-town')} onChange={e => setAddressTown(e.target.value)} value={addressTown} {...(lockAddress ? { readOnly: true } : {})} />
              </label>
              <div className="p-2.5">
                <button className={"btn btn-secondary text-white rounded-full"} onClick={handleAddressCreation} disabled={!canNewAddressBeSubmitted() || lockAddress} >
                  <Plus height={14} width={14} className="stroke-white" />
                  {t("cart-delivery-address-new-save")}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2.5 w-full">
              <div className="p-2.5">
                {t("new-address-when-logged-in-only")}
              </div>
              <div className="p-2.5">
                <Link href={`/kundenkonto/login?continue=${encodeURIComponent(pathname)}`}>
                  <button className={"btn btn-secondary text-white rounded-full"}>
                    <UserSolid height={14} width={14} className="stroke-white" />
                    {t("login-or-signup")}
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </dialog>
    </div >
  )
}
